import { Link } from 'react-router-dom';
import { Button } from './Button';
import type { WysiwygSaveState } from './saveState';
import type { AgentRunState } from '../../lib/agentProtocol';

export interface SavebarBackTarget {
  to: string;
  label: string;
}

export type SavebarMode = 'edit' | 'preview';

export interface SavebarProps {
  back?: SavebarBackTarget;
  saveState: WysiwygSaveState;
  dirty: boolean;
  onSave: () => void | Promise<void>;
  onHistoryOpen?: () => void;
  /** When provided with `onHistoryOpen`, drives `aria-expanded` on History. */
  historyOpen?: boolean;
  saveStateLabel?: (state: WysiwygSaveState, dirty: boolean) => string;
  saveButtonLabel?: (state: WysiwygSaveState, dirty: boolean) => string;
  testIdPrefix?: string;
  /**
   * Editor-mode toggle (Edit | Preview). When `onModeChange` is provided the
   * segmented control renders between the state indicator and History; when
   * omitted the savebar renders without it (back-compat for callers that
   * don't need source-mode toggling).
   */
  mode?: SavebarMode;
  onModeChange?: (mode: SavebarMode) => void;
  /**
   * Right-rail (inspector) show/hide toggle. When `onRailToggle` is provided
   * the savebar renders a "Show details" / "Hide details" button whose label is
   * driven by `railOpen`. When omitted the button is suppressed.
   */
  railOpen?: boolean;
  onRailToggle?: () => void;
  /**
   * Embedded agent-chat toggle (Phase 5). When `onChatToggle` is provided the
   * savebar renders a "Agent" toggle — a peer of the inspector and History
   * toggles — whose `aria-expanded` is driven by `chatOpen`. Omitted ⇒ no
   * toggle (the plain web build never passes it; the IDE build does).
   */
  chatOpen?: boolean;
  onChatToggle?: () => void;
  /**
   * Run state of the shared agent session, surfaced as a dot on the chat
   * toggle so the user sees `running…` even with the chat collapsed. Defaults
   * to `'idle'` when the toggle is shown without a state.
   */
  chatRunState?: AgentRunState;
}

export function Savebar({
  back,
  saveState,
  dirty,
  onSave,
  onHistoryOpen,
  historyOpen,
  saveStateLabel = defaultSaveStateLabel,
  saveButtonLabel = defaultSaveButtonLabel,
  testIdPrefix,
  mode,
  onModeChange,
  railOpen,
  onRailToggle,
  chatOpen,
  onChatToggle,
  chatRunState
}: SavebarProps) {
  const tid = (suffix: string) =>
    testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined;

  return (
    <div className="pn-savebar" data-testid={tid('savebar')}>
      <div className="pn-savebar-left">
        {back ? (
          <Link
            to={back.to}
            state={{ routeTransition: 'back' }}
            data-testid={tid('back')}
            className="pn-savebar-back"
          >
            ← {back.label}
          </Link>
        ) : null}
      </div>
      <div className="pn-savebar-right">
        <span
          className="pn-savestate"
          data-state={saveState}
          data-testid={tid('savestate')}
        >
          {saveStateLabel(saveState, dirty)}
        </span>
        {onModeChange ? (
          <div
            className="pn-savebar-mode"
            role="group"
            aria-label="Editor mode"
            data-testid={tid('mode')}
          >
            <button
              type="button"
              className="pn-savebar-mode-btn"
              data-mode="edit"
              aria-pressed={mode === 'edit'}
              data-testid={tid('mode-edit')}
              onClick={() => onModeChange('edit')}
            >
              Edit
            </button>
            <button
              type="button"
              className="pn-savebar-mode-btn"
              data-mode="preview"
              aria-pressed={mode === 'preview'}
              data-testid={tid('mode-preview')}
              onClick={() => onModeChange('preview')}
            >
              Preview
            </button>
          </div>
        ) : null}
        {onHistoryOpen ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onHistoryOpen}
            aria-expanded={historyOpen}
            data-testid={tid('history-open')}
          >
            History
          </Button>
        ) : null}
        {onChatToggle ? (
          <button
            type="button"
            className="pn-savebar-chat-toggle"
            onClick={onChatToggle}
            aria-expanded={chatOpen}
            data-testid={tid('chat-toggle')}
          >
            <span
              className="pn-savebar-chat-dot"
              data-run-state={chatRunState ?? 'idle'}
              data-testid={tid('chat-dot')}
              aria-hidden="true"
            />
            Agent
          </button>
        ) : null}
        {onRailToggle ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRailToggle}
            aria-expanded={railOpen}
            aria-controls={tid('rail')}
            data-testid={tid('rail-toggle')}
          >
            {railOpen ? 'Hide details' : 'Show details'}
          </Button>
        ) : null}
        <Button
          variant="primary"
          size="sm"
          onClick={() => void onSave()}
          disabled={!dirty || saveState === 'saving'}
          data-testid={tid('save')}
          data-state={saveState}
        >
          {saveButtonLabel(saveState, dirty)}
        </Button>
      </div>
    </div>
  );
}

function defaultSaveStateLabel(
  state: WysiwygSaveState,
  dirty: boolean
): string {
  switch (state) {
    case 'saving':
      return 'Saving…';
    case 'saved':
      return dirty ? 'Unsaved changes' : 'Saved';
    case 'conflict':
      return 'Conflict';
    case 'failed':
      return 'Save failed';
    default:
      return dirty ? 'Unsaved changes' : 'Idle';
  }
}

function defaultSaveButtonLabel(
  state: WysiwygSaveState,
  dirty: boolean
): string {
  switch (state) {
    case 'saving':
      return 'Saving…';
    case 'saved':
      return dirty ? 'Save' : 'Saved';
    case 'conflict':
      return 'Save';
    case 'failed':
      return 'Retry save';
    default:
      return 'Save';
  }
}
